/**
 * BundleCheckoutProtection
 *
 * Prevents checkout with incomplete bundles (1-2 items instead of 3).
 * Detection: Cart items with properties._bundle_id
 * Complete bundle: 3+ items sharing the same _bundle_id
 *
 * Architecture:
 * - Delegated capture listeners on window/document (survive DOM replacement)
 * - Re-validates on every cart update via 'updated.ecomify.cart'
 * - Shows modal with options: remove items OR complete bundle
 *
 * @see PUPRING-CHANGELOG-bundle-builder.md for implementation history
 */

class BundleCheckoutProtection {
  static BUNDLE_COLLECTION_URL = '/collections/buy-3-for-99';
  static REQUIRED_BUNDLE_SIZE = 3;
  static CHECKOUT_SELECTOR = '.btn-checkout[name="checkout"]';
  static CHECKOUT_LINK_SELECTOR = 'a[href$="/checkout"], [data-save-checkout]';

  constructor() {
    this.isValid = true;
    this.bundleItems = [];
    this.bundleGroups = {};
    this.isProcessing = false;
  }

  static getInstance() {
    if (!window._bundleProtection) {
      window._bundleProtection = new BundleCheckoutProtection();
    }
    return window._bundleProtection;
  }

  async init() {
    // Delegated listeners - survive DOM replacement
    this.setupEventDelegation();
    this.bindCartEvents();
    await this.validateCart(); // Ensure validation completes before allowing interactions
  }

  setupEventDelegation() {
    // Intercept checkout submissions only - product forms (/cart/add) and
    // cart updates (/cart/change) are never blocked. Capture phase runs first.
    document.addEventListener('submit', (e) => {
      if (this.isValid || !this.isCheckoutSubmit(e)) return;
      this.blockCheckout(e);
    }, true);

    // Checkout button and checkout link clicks (incl. locale-prefixed links and
    // the save-cart sheet), captured on window so this runs before other
    // document-level checkout handlers. The decision must stay synchronous:
    // preventDefault after an await has no effect.
    window.addEventListener('click', (e) => {
      if (this.isValid || !this.isCheckoutControl(e.target)) return;
      this.blockCheckout(e);
    }, true);
  }

  isCheckoutControl(target) {
    const { CHECKOUT_SELECTOR, CHECKOUT_LINK_SELECTOR } = BundleCheckoutProtection;
    return !!target?.closest?.(`${CHECKOUT_SELECTOR}, ${CHECKOUT_LINK_SELECTOR}`);
  }

  isCheckoutSubmit(e) {
    const form = e.target;
    if (!form?.matches?.('form')) return false;
    if (e.submitter) return e.submitter.matches('[name="checkout"]');
    return !!form.querySelector(BundleCheckoutProtection.CHECKOUT_SELECTOR);
  }

  blockCheckout(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.showModal();
  }

  bindCartEvents() {
    // Re-validate after every cart update
    window.addEventListener('updated.ecomify.cart', (e) => {
      this.validateCart(e.detail?.cart);
    });

    // Modal button handlers (event delegation)
    document.addEventListener('click', (e) => {
      // BUG-078 FIX: Use unique selector to avoid collision with Summary page's Complete Order button
      if (e.target.closest('[data-bundle-protection-complete]')) {
        this.redirectToBundle();
      }
      if (e.target.closest('[data-bundle-remove]')) {
        this.removeBundleItems();
      }
    });
  }

  async validateCart(cartData = null) {
    let cart = cartData || window.cartData;

    // No cart snapshot: read it. If the read fails (network, 429, bad JSON),
    // fail open and keep the last known result so shopping is never blocked.
    if (!cart?.items) {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`Cart fetch failed (${response.status})`);
        cart = await response.json();
        if (!Array.isArray(cart?.items)) throw new Error('Cart response has no items');
        window.cartData = cart;
      } catch {
        return;
      }
    }

    // Empty cart is valid (no bundle items to check)
    if (!cart?.items?.length) {
      this.isValid = true;
      this.bundleItems = [];
      this.bundleGroups = {};
      this.updateCheckoutButtons();
      return;
    }

    // Group items by _bundle_id property
    this.bundleGroups = {};
    this.bundleItems = [];

    cart.items.forEach(item => {
      const bundleId = item.properties?.['_bundle_id'];
      if (bundleId) {
        if (!this.bundleGroups[bundleId]) {
          this.bundleGroups[bundleId] = [];
        }
        this.bundleGroups[bundleId].push(item);
      }
    });

    // Check each bundle group - collect incomplete ones
    let hasIncompleteBundle = false;

    Object.entries(this.bundleGroups).forEach(([bundleId, items]) => {
      // Count unique main products (exclude engraving/upsells by checking _bundle_position)
      const mainProducts = items.filter(item =>
        item.properties?.['_bundle_position'] !== undefined
      );

      if (mainProducts.length > 0 && mainProducts.length < BundleCheckoutProtection.REQUIRED_BUNDLE_SIZE) {
        hasIncompleteBundle = true;
        this.bundleItems.push(...items); // Include all items from incomplete bundle
      }
    });

    this.isValid = !hasIncompleteBundle;
    this.updateCheckoutButtons();
  }

  updateCheckoutButtons() {
    // Update all checkout buttons (handles DOM replacement)
    document.querySelectorAll('.btn-checkout').forEach(btn => {
      btn.classList.toggle('bundle-incomplete', !this.isValid);
    });
  }

  showModal() {
    if (this.isProcessing) return;

    const modal = document.getElementById('bundle-protection-modal');
    if (!modal) {
      console.error('[BundleProtection] Modal not found');
      return;
    }

    // Update message
    const detailsEl = modal.querySelector('[data-bundle-details]');
    if (detailsEl) {
      const mainProducts = this.bundleItems.filter(item =>
        item.properties?.['_bundle_position'] !== undefined
      );
      const have = mainProducts.length;
      const need = BundleCheckoutProtection.REQUIRED_BUNDLE_SIZE - have;
      detailsEl.textContent = `You have ${have} of 3 required products. Add ${need} more different product${need > 1 ? 's' : ''} to complete your bundle.`;
    }

    bootstrap.Modal.getOrCreateInstance(modal).show();
  }

  async removeBundleItems() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const modal = document.getElementById('bundle-protection-modal');
    const removeBtn = modal?.querySelector('[data-bundle-remove]');

    // Show loading state
    if (removeBtn) {
      removeBtn.disabled = true;
      removeBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Removing...';
    }

    // Build updates object with all bundle item keys set to 0
    const updates = {};
    this.bundleItems.forEach(item => {
      updates[item.key] = 0;
    });

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        bootstrap.Modal.getInstance(modal)?.hide();
        // Trigger cart refresh
        await window.refreshCartContents?.(response);
      } else {
        throw new Error('Failed to update cart');
      }
    } catch (error) {
      console.error('[BundleProtection] Remove failed:', error);
      alert('Failed to remove items. Please try again.');
    } finally {
      this.isProcessing = false;
      if (removeBtn) {
        removeBtn.disabled = false;
        removeBtn.textContent = 'Remove Bundle Items';
      }
    }
  }

  redirectToBundle() {
    const modal = document.getElementById('bundle-protection-modal');
    bootstrap.Modal.getInstance(modal)?.hide();
    window.location.href = BundleCheckoutProtection.BUNDLE_COLLECTION_URL;
  }

  cleanup() {
    this.bundleItems = [];
    this.bundleGroups = {};
    this.isValid = true;
    this.isProcessing = false;
  }

  reinit() {
    this.cleanup();
    this.validateCart();
  }
}

// Initialize on DOM ready
const initBundleProtection = () => {
  BundleCheckoutProtection.getInstance().init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleProtection);
} else {
  initBundleProtection();
}

// Theme editor support
document.addEventListener('shopify:section:load', () => {
  BundleCheckoutProtection.getInstance().reinit();
});

// Expose for debugging
window.BundleCheckoutProtection = BundleCheckoutProtection;
